import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, FormControl, FormHelperText } from '@mui/material';
import api from '../../utils/api';

const LEAD_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export function formatLeadCoupleLabel(lead) {
  if (!lead) return '';
  const name = lead.name || '';
  const email = lead.email || '';
  let label = name && email ? `${name} (${email})` : (name || email || '');
  if (lead.wedding) {
    const weddingLabel = lead.wedding?.name || lead.wedding?.accessCode || 'couple';
    label = label ? `${label} (${weddingLabel})` : weddingLabel;
  }
  return label || 'Lead';
}

export default function LeadCoupleAutocomplete({
  businessId,
  value = '',
  onChange,
  onLeadChange,
  label = 'Select couple',
  required = false,
  disabled = false,
  helperText,
  allowNone = false,
  selectable = true,
  fullWidth = true,
  sx,
  margin = 'none',
}) {
  const [leads, setLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('');
  const [leadPage, setLeadPage] = useState(1);
  const [hasMoreLeads, setHasMoreLeads] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const resolvedLeadIdRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedLeadSearch(leadSearch), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [leadSearch]);

  useEffect(() => {
    setLeadPage(1);
    setHasMoreLeads(true);
  }, [debouncedLeadSearch, businessId]);

  useEffect(() => {
    if (!businessId) {
      setLeads([]);
      return undefined;
    }
    if (!hasMoreLeads && leadPage > 1) return undefined;

    let cancelled = false;

    const loadLeads = async () => {
      try {
        setLoadingLeads(true);
        const params = {
          businessId,
          page: leadPage,
          limit: LEAD_PAGE_SIZE,
          search: debouncedLeadSearch || undefined,
        };
        if (selectable) {
          params.selectable = 'true';
        }

        const res = await api.get('/api/leads', { params });
        if (cancelled) return;

        const newLeads = res.data?.leads || [];
        setLeads((prev) => {
          if (leadPage === 1) return newLeads;
          const existingIds = new Set(prev.map((l) => l._id));
          const merged = [...prev];
          newLeads.forEach((l) => {
            if (!existingIds.has(l._id)) merged.push(l);
          });
          return merged;
        });
        setHasMoreLeads(newLeads.length >= LEAD_PAGE_SIZE);
      } catch {
        if (leadPage === 1) setLeads([]);
        setHasMoreLeads(false);
      } finally {
        if (!cancelled) setLoadingLeads(false);
      }
    };

    loadLeads();
    return () => {
      cancelled = true;
    };
  }, [businessId, leadPage, debouncedLeadSearch, hasMoreLeads, selectable]);

  useEffect(() => {
    if (!value || !businessId) return undefined;

    const matched = leads.find((l) => String(l._id) === String(value));
    if (matched) {
      if (resolvedLeadIdRef.current !== value) {
        resolvedLeadIdRef.current = value;
        onLeadChange?.(matched);
      }
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/leads/${value}`);
        if (cancelled) return;
        const lead = res.data?.lead ?? res.data;
        if (!lead?._id) return;
        setLeads((prev) => (prev.some((l) => l._id === lead._id) ? prev : [lead, ...prev]));
        resolvedLeadIdRef.current = value;
        onLeadChange?.(lead);
      } catch {
        // Non-blocking: pre-selected lead may no longer exist
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, businessId, leads, onLeadChange]);

  const selectedLead = value
    ? leads.find((l) => String(l._id) === String(value)) || null
    : null;

  const handleChange = (_, newValue) => {
    if (!newValue) {
      onChange?.('');
      onLeadChange?.(null);
      setLeadSearch('');
      setLeadPage(1);
      setHasMoreLeads(true);
      resolvedLeadIdRef.current = null;
      return;
    }
    onChange?.(newValue._id);
    onLeadChange?.(newValue);
    resolvedLeadIdRef.current = newValue._id;
  };

  return (
    <FormControl fullWidth={fullWidth} required={required} sx={sx} margin={margin}>
      <Autocomplete
        options={leads}
        getOptionLabel={formatLeadCoupleLabel}
        isOptionEqualToValue={(option, val) => !!val && option._id === val._id}
        value={selectedLead}
        onChange={handleChange}
        onInputChange={(event, newInput, reason) => {
          if (reason === 'reset') return;
          setLeadSearch(newInput || '');
        }}
        loading={loadingLeads}
        disabled={disabled}
        disableClearable={!allowNone}
        filterOptions={(options) => options}
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 100 &&
              hasMoreLeads &&
              !loadingLeads
            ) {
              setLeadPage((p) => p + 1);
            }
          },
          style: { maxHeight: 320 },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            placeholder={allowNone ? 'Type to search or leave empty' : 'Type to search'}
          />
        )}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
